import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_WIDTH = 180;

/*
 * A row's overflow actions behind "More". Menu-button pattern: Enter/Space/ArrowDown open it on the
 * first item, arrows move, Escape closes and returns focus, Tab or a click outside closes. The list
 * is portalled and fixed under the button, so no table cell or card clips it.
 */
const ActionsMenu = ({ items, label }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btn = useRef(null);
  const menu = useRef(null);
  const id = useId();

  const close = (refocus) => {
    setOpen(false);
    setPos(null);
    if (refocus && btn.current) btn.current.focus();
  };
  const focus_item = (i) => {
    const list = menu.current ? [...menu.current.querySelectorAll('[role="menuitem"]')] : [];
    if (list.length) list[(i + list.length) % list.length].focus();
  };

  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    setPos({ top: r.bottom + 4, left });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (pos) focus_item(0);
    const outside = (e) => {
      if (btn.current?.contains(e.target) || menu.current?.contains(e.target)) return;
      close(false);
    };
    // the menu is fixed: a scroll or resize would leave it behind its button
    const away = () => close(false);
    document.addEventListener('mousedown', outside);
    window.addEventListener('scroll', away, true);
    window.addEventListener('resize', away);
    return () => {
      document.removeEventListener('mousedown', outside);
      window.removeEventListener('scroll', away, true);
      window.removeEventListener('resize', away);
    };
  }, [open, pos]);

  const on_menu_key = (e) => {
    const list = [...menu.current.querySelectorAll('[role="menuitem"]')];
    const at = list.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focus_item(at + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focus_item(at - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focus_item(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focus_item(list.length - 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  return (
    <>
      <button
        ref={btn}
        type="button"
        className="tp-pe-act-btn tp-pe-more-btn"
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={open ? id : undefined}
        aria-label={label}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        More
      </button>
      {open
        ? createPortal(
            // eslint-disable-next-line jsx-a11y/interactive-supports-focus
            <div
              ref={menu}
              id={id}
              role="menu"
              aria-label={label}
              className="tp-pe-menu"
              style={
                pos ? { top: pos.top, left: pos.left, width: MENU_WIDTH } : { visibility: 'hidden' }
              }
              onKeyDown={on_menu_key}
            >
              {items.map((it) => (
                <button
                  key={it.key}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  className="tp-pe-menu-item"
                  aria-label={it.aria_label}
                  onClick={() => {
                    close(true);
                    it.on_select();
                  }}
                >
                  {it.text}
                </button>
              ))}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default ActionsMenu;
